// SPDX-License-Identifier: Elastic-2.0
/**
 * RoutineCard — one routine row on the /routines page (T-026).
 *
 * Per task spec § 2:
 *   - Header: routine name (Inter 500 18px) + active/inactive pill
 *     (sage if active, gray if inactive).
 *   - Body: trigger summary + action summary (both Inter 14px secondary).
 *   - Buttons: "Run now" (primary sm) + kebab menu (Disable/Enable + Delete
 *     with confirm).
 *
 * Visual: rounded 18px elevated surface (matches design tokens —
 * `--bg-elev-1`, `--shadow-2`). State pill uses the same sage tint that
 * `SuggestionCard` uses for its `.accepted` state (no design token yet —
 * see SuggestionCard.module.css line 33).
 *
 * Behaviour:
 *   - Trigger: pure callback up to the page.
 *   - Toggle / delete: also callbacks up to the page. Delete opens an
 *     in-card confirm flow (no Modal mount) — keeps the card self-
 *     contained and lets keyboard users tab through the confirm without
 *     focus-trap surprises.
 *   - Kebab menu: light-weight controlled-open popover. Closes on outside
 *     click + Escape. Both items are real buttons (not links), so screen
 *     readers announce them correctly.
 *
 * Accessibility:
 *   - The state pill carries `aria-label` so it's announced as
 *     "Routine: {name}, active".
 *   - Confirm row uses `role="alertdialog"`-style copy via plain markup;
 *     we don't mount a real dialog because the inline confirm covers the
 *     same intent without the modal overhead. The cancel button is the
 *     default-focus so Esc/Enter behave predictably.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon/Icon";

import type { Routine } from "@/api/gateway/models/Routine";

import styles from "./RoutinesListPage.module.css";

export interface RoutineCardProps {
  routine: Routine;
  /** Called when the user clicks "Run now". */
  onTrigger: (routine: Routine) => void;
  /** Called with the desired new active state. */
  onToggle: (routine: Routine, nextActive: boolean) => void;
  /** Called when the user confirms delete. */
  onDelete: (routine: Routine) => void;
  /**
   * Whether a mutation is in flight for this routine — disables the
   * controls so the user can't double-fire.
   */
  busy?: boolean;
}

export function RoutineCard({
  routine,
  onTrigger,
  onToggle,
  onDelete,
  busy = false,
}: RoutineCardProps): ReactElement {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);

  // Close kebab on outside click + Escape.
  useEffect(() => {
    if (!menuOpen) return;
    function handlePointer(event: MouseEvent): void {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  // When the confirm row opens, move focus to the cancel button so a
  // stray Enter doesn't accidentally delete.
  useEffect(() => {
    if (confirmDelete) {
      cancelDeleteRef.current?.focus();
    }
  }, [confirmDelete]);

  const pillLabel = routine.active ? t("routines.active") : t("routines.inactive");
  const pillClass = routine.active ? styles.pillActive : styles.pillInactive;

  return (
    <article
      ref={cardRef}
      className={styles.card}
      data-testid="routine-card"
      data-routine-id={routine.id}
      data-active={routine.active}
    >
      <header className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{routine.name}</h3>
        <span
          className={[styles.pill, pillClass].join(" ")}
          aria-label={`${routine.name}: ${pillLabel}`}
          data-testid="routine-pill"
        >
          {pillLabel}
        </span>
      </header>

      <div className={styles.cardBody}>
        <p className={styles.summaryLine} data-testid="routine-trigger-summary">
          {routine.trigger_summary}
        </p>
        <p className={styles.summaryLine} data-testid="routine-action-summary">
          {routine.action_summary}
        </p>
      </div>

      {confirmDelete ? (
        <div
          className={styles.confirmRow}
          role="group"
          aria-label={t("routines.delete_confirm")}
          data-testid="routine-delete-confirm"
        >
          <p className={styles.confirmText}>{t("routines.delete_confirm")}</p>
          <div className={styles.confirmActions}>
            <Button
              ref={cancelDeleteRef}
              variant="secondary"
              size="sm"
              onClick={() => setConfirmDelete(false)}
              data-testid="routine-delete-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setConfirmDelete(false);
                onDelete(routine);
              }}
              disabled={busy}
              data-testid="routine-delete-confirm-yes"
            >
              {t("routines.delete")}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.cardActions}>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onTrigger(routine)}
            disabled={busy}
            data-testid="routine-trigger"
          >
            {t("routines.trigger_now")}
          </Button>
          <div className={styles.kebabWrap}>
            <button
              type="button"
              className={styles.kebab}
              aria-label={t("routines.kebab")}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              disabled={busy}
              data-testid="routine-kebab"
            >
              <Icon name="plus" size={18} title={t("routines.kebab")} />
            </button>
            {menuOpen ? (
              <div
                className={styles.menu}
                role="menu"
                data-testid="routine-kebab-menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  className={styles.menuItem}
                  onClick={() => {
                    setMenuOpen(false);
                    onToggle(routine, !routine.active);
                  }}
                  data-testid="routine-toggle"
                >
                  {routine.active ? t("routines.disable") : t("routines.enable")}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className={[styles.menuItem, styles.menuItemDanger].join(" ")}
                  onClick={() => {
                    setMenuOpen(false);
                    setConfirmDelete(true);
                  }}
                  data-testid="routine-delete"
                >
                  {t("routines.delete")}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </article>
  );
}

export default RoutineCard;
